




import {
  defineComponent,
  onBeforeUnmount,
  onMounted,
  PropType,
  reactive,
  watch,
} from '@vue/composition-api';
import Chart from 'chart.js';
import { dtFormat } from '@/lib/dateHelper';

// グラフにおける Y 軸ID
const Y_AXIS_VELOCITY = 'yAxisVelocity';

export class SensorData {
  xLabels: Date[];
  vs: number[];

  constructor() {
    this.xLabels = [];
    this.vs = [];
  }
}

export default defineComponent({
  props: {
    sensorData: {
      type: Object as PropType<SensorData>,
      default: () => { return new SensorData(); },
      required: true,
    },
    reportId: {
      type: Number,
      required: true,
    },
  },
  setup(props) {
    // report.idを使って一意のIDを生成
    const chartId = `mySensorChart_${props.reportId}`;
    // センサーデータ
    const localSensorData = reactive<SensorData>({
      xLabels: [...props.sensorData.xLabels],
      vs: [...props.sensorData.vs],
    });

    // chart.js の描画データ
    const data = {
      labels: localSensorData.xLabels,
      datasets: [
        {
          label: '速度',
          data: localSensorData.vs,
          type: 'line',
          fill: true,
          yAxisID: Y_AXIS_VELOCITY,
          tension: 0,
          backgroundColor: 'rgba(75,192,192,0.4)',
          borderColor: 'rgba(75,192,192,1)',
          borderWidth: 2,
          borderCapStyle: 'butt' as const,
          borderJoinStyle: 'miter' as const,
          pointBackgroundColor: 'rgba(75,192,192,1)',
          pointBorderWidth: 0,
          pointHoverRadius: 5,
          pointHoverBackgroundColor: 'rgba(75,192,192,1)',
          pointHoverBorderColor: 'rgba(220,220,220,1)',
          pointHoverBorderWidth: 2,
          pointRadius: 1,
          pointHitRadius: 10,
          spanGaps: false,
        },
      ],
    };

    // chart.js の描画オプション
    const options: any = {
      animation: {
        duration: 0,
      },
      responsive: false,
      title: {
        display: false,
      },
      tooltips: {
        mode: 'index' as const,
        callbacks: {
          title: (tooltipItems: any[]) => {
            if (tooltipItems.length > 0) {
              const dt = localSensorData.xLabels[tooltipItems[0].index];
              return dtFormat(dt, 'H:MM:SS');
            }
            return '';
          },
          // 速度データのラベルにだけ単位をつける
          label: (tooltipItem: any, data: any) => {
            var label = data.datasets[tooltipItem.datasetIndex].label || '';
            if (label) {
              label += ': ';
            }
            if (tooltipItem.yLabel !== null) {
              // 0 番目の速度 Y軸のラベルを張り替える
              const isVelocityData = tooltipItem.datasetIndex === 0;
              const yLabel = tooltipItem.yLabel;
              label += isVelocityData ? `${yLabel} km/h` : `${yLabel}`;
            }
            return label;
          },
        },
      },
      scales: {
        xAxes: [{
          type: 'time',
          position: 'top',
          time: {
            unit: 'minute',
            stepSize: 20,
            displayFormats: {
              day: 'MM-DD',
              hour: 'H:mm',
              minute: 'H:mm',
              second: 'HH:mm:ss',
            },
          },
          ticks: {
            maxTicksLimit: 10,
          },
        }],
        yAxes: [{
          id: Y_AXIS_VELOCITY,
          display: true,
          beginAtZero: true,
          scaleLabel: {
            display: true,
            labelString: 'km/h',
          },
        }],
      },
    };

    let myChart: Chart;
    const createChart = () => {
      if (!myChart) {
        const canvasRef = document.getElementById(chartId) as HTMLCanvasElement;
        if (canvasRef === null) return;
        const canvas = canvasRef.getContext('2d');
        if (canvas === null) return;
        myChart = new Chart(canvas, {
          type: 'line',
          data: data,
          options: options,
        });
        myChart.resize();
      }
      return myChart;
    };

    onBeforeUnmount(() => {
      if (myChart) {
        myChart.destroy();
      }
    });

    onMounted(() => {
      createChart();
    });

    watch(() => props.sensorData, (newSensorData) => {
      // ローカルデータを更新
      localSensorData.xLabels = [...newSensorData.xLabels];
      localSensorData.vs = [...newSensorData.vs];

      // チャートデータを更新
      data.labels = localSensorData.xLabels;
      data.datasets[0].data = localSensorData.vs;

      if (myChart) myChart.update();
    }, { deep: true });

    return {
      chartId,
    };
  },
});
